import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getLesson } from "../lib/homeKeyCurriculum";
import {
  getUserId,
  subscribeProgress,
  saveProgress,
  emptyProgress,
} from "../lib/homeKeyProgress";
import { ROOMS, roomOpen, practiceVisit } from "../lib/homeKeyPractice";
import EchoRoom from "../components/homeKey/practice/EchoRoom";
import JobsRoom from "../components/homeKey/practice/JobsRoom";
import PocketRoom from "../components/homeKey/practice/PocketRoom";
import "../styles/homekey.css";

/* ============================================================
   /home-key/practice — the room hall. The course's optional gym:
   three rooms, each unlocking with course progress (dimmed with a
   teaser before then, tile-map style). One route; the open room is
   internal state, not a sub-route. No scores persist — only a soft
   { visits, lastAt, tier } note per room, via practiceVisit.
   ============================================================ */

const HALL_SUBTITLE =
  "The course's gym, open after hours. Short sits — a few reps, a warm " +
  "summary, no scores kept. You leave warmer than you came.";

const HomeKeyPractice = () => {
  const [progress, setProgress] = useState(emptyProgress);
  const [loaded, setLoaded] = useState(false);
  const [roomId, setRoomId] = useState(null);

  useEffect(() => {
    const unsub = subscribeProgress(getUserId(), (p) => {
      // A subscription echo can lag the visit we just recorded locally —
      // don't let it wipe this session's practice stats.
      setProgress((prev) =>
        prev.practice && !p.practice ? { ...p, practice: prev.practice } : p
      );
      setLoaded(true);
    });
    return unsub;
  }, []);

  // Note a visit (and, when given, the tier the learner chose) — the only
  // thing the practice rooms ever persist.
  const recordVisit = (id, tier) => {
    setProgress((prev) => {
      const next = practiceVisit(prev, id, tier);
      saveProgress(getUserId(), next);
      return next;
    });
  };

  const enterRoom = (id) => {
    recordVisit(id);
    setRoomId(id);
  };

  if (!loaded) {
    return (
      <div className="hk-root">
        <div className="hk-studio hk-practice">
          <p className="hk-loading">unlocking the rooms…</p>
        </div>
      </div>
    );
  }

  const room = ROOMS.find((r) => r.id === roomId) || null;
  const practiceStats = progress.practice || {};

  return (
    <div className="hk-root">
      <div className="hk-studio hk-practice">
        <header className="hk-studio-header">
          {room ? (
            <button
              type="button"
              className="hk-back-link"
              onClick={() => setRoomId(null)}
            >
              ← back to the hall
            </button>
          ) : (
            <Link to="/home-key" className="hk-back-link">
              ← back to the map
            </Link>
          )}
          <h1 className="hk-studio-title">
            {room ? room.title : "Practice rooms"}
          </h1>
          <p className="hk-studio-subtitle">{room ? room.blurb : HALL_SUBTITLE}</p>
        </header>

        {!room && (
          <div className="hk-rooms">
            {ROOMS.map((r) => {
              const open = roomOpen(r.id, progress.completed);
              if (!open) {
                const gate = getLesson(r.opensAfter);
                return (
                  <div key={r.id} className="hk-room-card hk-room-card--locked">
                    <span className="hk-room-title">{r.title}</span>
                    <span className="hk-room-teaser">{r.teaser}</span>
                    <span className="hk-room-gate">
                      opens after “{gate ? gate.title : r.opensAfter}”
                    </span>
                  </div>
                );
              }
              const stats = practiceStats[r.id];
              return (
                <button
                  key={r.id}
                  type="button"
                  className="hk-room-card"
                  onClick={() => enterRoom(r.id)}
                >
                  <span className="hk-room-title">{r.title}</span>
                  <span className="hk-room-teaser">{r.blurb}</span>
                  <span className="hk-room-cta">
                    {stats && stats.visits ? "step back in" : "step in"}
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {room && room.id === "echo" && (
          <EchoRoom
            completed={progress.completed}
            savedTier={(practiceStats.echo || {}).tier}
            onTier={(tierId) => recordVisit("echo", tierId)}
            onExit={() => setRoomId(null)}
          />
        )}
        {room && room.id === "jobs" && (
          <JobsRoom
            completed={progress.completed}
            onExit={() => setRoomId(null)}
          />
        )}
        {room && room.id === "pocket" && (
          <PocketRoom
            keepers={progress.keepers}
            onExit={() => setRoomId(null)}
          />
        )}

        {!room && (
          <footer className="hk-studio-footer">
            <p>
              Rooms open as the course does — each one only ever asks about
              music you've already met.
            </p>
          </footer>
        )}
      </div>
    </div>
  );
};

export default HomeKeyPractice;
